import { useId, useState } from 'react';
import { Slider as BaseSlider } from '@base-ui/react/slider';
import { RiAddLine, RiSubtractLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { CAPS_POPOVER, useFieldControlProps } from './field';
import { IconButton } from './icon-button';

export interface SliderProps {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  /** Fires once the user releases the thumb (commit / undo entry). */
  onValueCommitted?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /**
   * `default` — track h6 r3 White/10, fill `#35b5ec`, thumb 16 white + 3px `#35b5ec` border (382:6664).
   * `steps` — "Matn o'lchami" (559:17463): h2 `#35b5ec` track, 6 px stop dots, thumb 18 white.
   */
  variant?: 'default' | 'steps';
  name?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Screen-reader text for the current value, e.g. `(v) => `${v}%``. */
  getAriaValueText?: (value: number) => string;
  className?: string;
}

/** Slider — Base UI Slider with the product track/thumb (spec §4.19). */
export function Slider({
  value,
  defaultValue,
  onValueChange,
  onValueCommitted,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  variant = 'default',
  name,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  getAriaValueText,
  className,
}: SliderProps) {
  const field = useFieldControlProps({ id, disabled });
  const steps = variant === 'steps' ? Math.floor((max - min) / step) : 0;
  return (
    <BaseSlider.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={(next) => onValueChange?.(Array.isArray(next) ? next[0]! : next)}
      onValueCommitted={(next) => onValueCommitted?.(Array.isArray(next) ? next[0]! : next)}
      min={min}
      max={max}
      step={step}
      disabled={field.disabled}
      name={name}
      // keep the thumb inside the track (it would otherwise overlap the −/+ buttons at the ends)
      thumbAlignment={variant === 'steps' ? 'center' : 'edge'}
      className={cn('w-full min-w-0 data-disabled:opacity-50', className)}
    >
      <BaseSlider.Control className={cn('relative flex w-full touch-none items-center select-none', variant === 'steps' ? 'h-20' : 'h-16')}>
        <BaseSlider.Track
          className={cn('relative w-full', variant === 'steps' ? 'h-2 rounded-1 bg-accent-2' : 'h-6 rounded-3 bg-field')}
        >
          {variant === 'default' && <BaseSlider.Indicator className="rounded-3 bg-accent-2" />}
          {variant === 'steps' &&
            Array.from({ length: steps + 1 }, (_, i) => (
              <span
                key={i}
                aria-hidden
                className="absolute top-1/2 size-6 -translate-1/2 rounded-full bg-accent-2"
                style={{ left: `${(i / Math.max(steps, 1)) * 100}%` }}
              />
            ))}
          <BaseSlider.Thumb
            id={field.id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy ?? field.labelId}
            getAriaValueText={getAriaValueText ? (_formatted, v) => getAriaValueText(v) : undefined}
            className={cn(
              'rounded-full bg-white outline-none transition-[scale] duration-150 data-dragging:scale-110',
              'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-accent-2',
              variant === 'steps'
                ? 'size-18 shadow-knob'
                : 'size-16 border-[0.1875rem] border-accent-2 data-disabled:border-line-strong',
              disabled ? 'cursor-not-allowed' : 'cursor-grab data-dragging:cursor-grabbing',
            )}
          />
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
}

export interface StepperSliderProps extends Omit<SliderProps, 'aria-label' | 'aria-labelledby' | 'variant'> {
  /** Caps caption; the formatted value is appended: "SHAFFOFLIK" + "(100%)". */
  label: string;
  value: number;
  onValueChange: (value: number) => void;
  /** Value shown in the caption. Default `String(v)`. */
  format?: (value: number) => string;
  decreaseLabel?: string;
  increaseLabel?: string;
}

/**
 * Stepper slider (382:6661 / 401:8949): caption `t-caps leading-auto tracking-caps` with the live value,
 * then a h32 row (gap 10): −32 · slider · +32 (White/10 r8, 16 icons).
 */
export function StepperSlider({
  label,
  value,
  onValueChange,
  format = String,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  decreaseLabel = 'kamaytirish',
  increaseLabel = 'oshirish',
  className,
  ...rest
}: StepperSliderProps) {
  const labelId = useId();
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v / step) * step));
  const nudge = (dir: 1 | -1) => {
    const next = clamp(value + dir * step);
    onValueChange(next);
    rest.onValueCommitted?.(next);
  };
  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-8', className)}>
      <span id={labelId} className={CAPS_POPOVER}>
        {label} ({format(value)})
      </span>
      <div className="flex h-32 items-center gap-10">
        <IconButton
          icon={RiSubtractLine}
          variant="field"
          size="md"
          aria-label={`${label}: ${decreaseLabel}`}
          disabled={disabled || value <= min}
          onClick={() => nudge(-1)}
        />
        <Slider
          {...rest}
          value={value}
          onValueChange={onValueChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-labelledby={labelId}
          getAriaValueText={rest.getAriaValueText ?? format}
        />
        <IconButton
          icon={RiAddLine}
          variant="field"
          size="md"
          aria-label={`${label}: ${increaseLabel}`}
          disabled={disabled || value >= max}
          onClick={() => nudge(1)}
        />
      </div>
    </div>
  );
}

export interface ValueSliderProps extends Omit<SliderProps, 'variant' | 'value' | 'onValueChange'> {
  value: number;
  onValueChange: (value: number) => void;
  /** Unit after the value box, e.g. "px" (400 12/17 White/50). */
  unit?: string;
}

/** Value slider (429:17218): slider + editable value box h32 r8 White/10 (600 13/18, centred) + unit; gap 10. */
export function ValueSlider({ value, onValueChange, unit, min = 0, max = 100, step = 1, disabled, className, ...rest }: ValueSliderProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft == null) return;
    const parsed = Number(draft.replace(',', '.'));
    if (Number.isFinite(parsed)) onValueChange(Math.min(max, Math.max(min, parsed)));
    setDraft(null);
  };
  return (
    <div className={cn('flex w-full min-w-0 items-center gap-10', className)}>
      <Slider {...rest} value={value} onValueChange={onValueChange} min={min} max={max} step={step} disabled={disabled} />
      <input
        inputMode="decimal"
        aria-label={rest['aria-label'] ? `${rest['aria-label']} — qiymat` : 'Qiymat'}
        disabled={disabled}
        value={draft ?? String(value)}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setDraft(null);
        }}
        className="h-32 w-52 shrink-0 rounded-8 border border-transparent bg-field text-center text-13 leading-18 font-semibold text-fg outline-none focus:border-focus disabled:opacity-50"
      />
      {unit && <span className="shrink-0 text-xs leading-17 tracking-normal text-white-500 light:text-black-500">{unit}</span>}
    </div>
  );
}
